import { defineStore } from 'pinia'
import { login, refreshToken } from '@/api/auth'
import type { LoginResponse } from '@/types/auth-types'

function setSentryUser(user: { id?: string; email: string; username: string } | null) {
  import('@sentry/vue').then(Sentry => Sentry.setUser(user))
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as LoginResponse | null,
    accessToken: '',
    refreshToken: '',
    loading: false
  }),

  actions: {
    async loginUser({ email, password }: { email?: string; password: string }) {
      this.loading = true
      try {
        const response = await login({ email, password })
        this.user = response
        this.setUserTokens(response)
        setSentryUser({
          id: response.userId,
          email: response.email,
          username: `${response.firstName} ${response.lastName}`,
        })
        return true
      } catch (err) {
        console.error('Login error:', err)
        throw err
      } finally {
        this.loading = false
      }
    },

    async refreshTokens() {
      try {
        const res = await refreshToken({
          accessToken: this.accessToken,
          refreshToken: this.refreshToken
        })
        this.setUserTokens(res)
      } catch (err) {
        console.error('Failed to refresh tokens', err)
        this.logout()
      }
    },

    logout() {
      this.user = null
      this.accessToken = ''
      this.refreshToken = ''
      localStorage.clear()
      setSentryUser(null)
    },

    setUserTokens(data: LoginResponse | { accessToken: string; refreshToken: string}) {
      if ('userId' in data) this.user = data
      this.accessToken = data.accessToken
      this.refreshToken = data.refreshToken
      localStorage.setItem('accessToken', data.accessToken)
      localStorage.setItem('refreshToken', data.refreshToken)
      localStorage.setItem('role', JSON.stringify(this.user?.role))
      localStorage.setItem('userId', JSON.stringify(this.user?.userId))
    }
  }
})
